/**
 * Vendor portal — Dashboard.
 * KPIs (revenue, orders, products, refunds), recent orders, alerts.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useVendor } from "@/lib/store/useVendor";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { formatPrice } from "@/store/storeCart";
import {
  TrendingUp,
  ShoppingBag,
  Package,
  Undo2,
  AlertTriangle,
  ExternalLink,
  Eye,
} from "lucide-react";

type Stats = {
  revenue30d: number;
  orders30d: number;
  productsActive: number;
  productsDraft: number;
  refundsPending: number;
};

type RecentOrder = {
  id: string;
  order_number: string;
  status: string;
  total_cents: number;
  created_at: string;
};

const VendorDashboard = () => {
  const { vendor, isSandbox } = useVendor();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recent, setRecent] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendor) return;
    let cancelled = false;
    (async () => {
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const [orders30, products, refunds, recentRows] = await Promise.all([
        supabase
          .from("store_orders")
          .select("total_cents, status", { count: "exact" })
          .eq("vendor_id", vendor.id)
          .gte("created_at", since)
          .in("status", ["paid", "fulfilled", "shipped", "delivered", "partially_refunded"]),
        supabase
          .from("store_products")
          .select("status")
          .eq("vendor_id", vendor.id),
        supabase
          .from("store_refund_requests")
          .select("id", { count: "exact", head: true })
          .eq("vendor_id", vendor.id)
          .eq("status", "pending"),
        supabase
          .from("store_orders")
          .select("id, order_number, status, total_cents, created_at")
          .eq("vendor_id", vendor.id)
          .order("created_at", { ascending: false })
          .limit(5),
      ]);
      if (cancelled) return;
      const revenue = (orders30.data ?? []).reduce(
        (sum, o: { total_cents: number }) => sum + o.total_cents,
        0,
      );
      const productsRows = (products.data ?? []) as { status: string }[];
      setStats({
        revenue30d: revenue,
        orders30d: orders30.count ?? 0,
        productsActive: productsRows.filter((p) => p.status === "active").length,
        productsDraft: productsRows.filter((p) => p.status === "draft").length,
        refundsPending: refunds.count ?? 0,
      });
      setRecent((recentRows.data ?? []) as RecentOrder[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [vendor]);

  if (!vendor) return null;

  const needsCommunity = !vendor.app_id;
  const needsStripe = !isSandbox && !vendor.stripe_charges_enabled;

  return (
    <div className="space-y-6">
      {(needsCommunity || needsStripe) && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Finish setting up your shop</AlertTitle>
          <AlertDescription className="space-y-1 mt-1">
            {needsCommunity && <p>· Choose a community on the Settings page.</p>}
            {needsStripe && <p>· Connect Stripe to start receiving payouts.</p>}
            <Button asChild size="sm" variant="outline" className="mt-2">
              <Link to="/store/vendor/settings">Open settings</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={TrendingUp}
          label="Revenue · 30d"
          value={loading ? null : formatPrice(stats?.revenue30d ?? 0)}
        />
        <KpiCard
          icon={ShoppingBag}
          label="Orders · 30d"
          value={loading ? null : String(stats?.orders30d ?? 0)}
        />
        <KpiCard
          icon={Package}
          label="Active products"
          value={loading ? null : String(stats?.productsActive ?? 0)}
          sub={loading ? undefined : `${stats?.productsDraft ?? 0} drafts`}
        />
        <KpiCard
          icon={Undo2}
          label="Pending refunds"
          value={loading ? null : String(stats?.refundsPending ?? 0)}
          highlight={(stats?.refundsPending ?? 0) > 0}
        />
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Recent orders</h2>
          <Button asChild size="sm" variant="ghost">
            <Link to="/store/vendor/orders">View all</Link>
          </Button>
        </div>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No orders yet. Once you publish products and a buyer checks out, orders will land here.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {recent.map((o) => (
              <Link
                key={o.id}
                to={`/store/vendor/orders/${o.id}`}
                className="flex items-center justify-between gap-4 py-3 hover:bg-muted/30 px-2 -mx-2 rounded-md transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(o.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="secondary" className="capitalize">
                    {o.status.replace(/_/g, " ")}
                  </Badge>
                  <span className="font-semibold">{formatPrice(o.total_cents)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 flex items-center justify-between">
        <div>
          <h2 className="font-semibold">Your public storefront</h2>
          <p className="text-sm text-muted-foreground mt-1">
            See what buyers see when they visit your shop.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to={`/store/vendor/${vendor.slug}`}>
            <Eye className="mr-2 h-4 w-4" /> Preview <ExternalLink className="ml-2 h-3 w-3" />
          </Link>
        </Button>
      </Card>
    </div>
  );
};

const KpiCard = ({
  icon: Icon,
  label,
  value,
  sub,
  highlight,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | null;
  sub?: string;
  highlight?: boolean;
}) => (
  <Card className={`p-4 ${highlight ? "border-primary/40 bg-primary/5" : ""}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </div>
    {value === null ? (
      <Skeleton className="h-8 w-20 mt-2" />
    ) : (
      <p className="text-2xl font-bold mt-2">{value}</p>
    )}
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </Card>
);

export default VendorDashboard;
